import React, { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MailCheckIcon, MailIcon } from "lucide-react";
import { AuthCard } from "../../components/auth/AuthCard";
import { TextField } from "../../components/ui/TextField";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { spring } from "../../utils/motion";
export function ForgotPassword() {
  const reduce = useReducedMotion() ?? false;
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);
  const send = (e?: FormEvent) => {
    e?.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Enter the email you signed up with.");
      return;
    }
    setError("");
    setStatus("sending");
    window.setTimeout(() => {
      setStatus("sent");
      setCooldown(30);
    }, 900);
  };
  return <AuthCard title={status === "sent" ? "Check your inbox" : "Reset your password"} description={status === "sent" ? undefined : "We'll email you a link to choose a new one."} footer={<Link to="/auth/signin" className="font-semibold text-ink underline underline-offset-4" data-id="element-1673" data-mp="3i18tf3dwoy0s">
          Back to sign in
        </Link>} data-id="element-1672" data-mp="ejgxxf0k9vxu">
      <AnimatePresence mode="wait" initial={false} data-id="element-1674" data-mp="3r26t59hbajdk">
        {status === "sent" ? <motion.div key="sent" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={spring.smooth} role="status" data-id="element-1675" data-mp="3n0bpvzftlvx5">
            <motion.span initial={reduce ? false : {
          scale: 0.6,
          rotate: -10
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={spring.bouncy} className="grid h-14 w-14 place-items-center rounded-card border-3 border-line bg-accent text-night" data-id="element-1676" data-mp="16dj1u0et9cjh">
              <MailCheckIcon className="h-7 w-7" data-id="element-1677" data-mp="3zuxnl662qz8" />
            </motion.span>
            <p className="mt-4 text-[15px] leading-relaxed text-ink-2" data-id="element-1678" data-mp="8xwdzyc7kc70">
              If an account exists for <strong className="text-ink" data-id="element-1679" data-mp="cxx44g8o6btw">{email}</strong>, a reset link is on its way. It expires in 30 minutes.
            </p>
            <div className="mt-6 flex flex-col gap-3" data-id="element-1680" data-mp="3hcy035oohvzd">
              <BrutalButton to="/auth/reset" full data-id="element-1681" data-mp="3hdptc9uszhmh">
                Open the reset link (demo)
              </BrutalButton>
              <button type="button" disabled={cooldown > 0} onClick={() => send()} className="focus-ring rounded-lg py-2 text-sm font-semibold text-ink-2 underline underline-offset-4 disabled:no-underline disabled:opacity-60" data-id="element-1682" data-mp="3lcf5jz43ut4j">
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
              </button>
            </div>
          </motion.div> : <motion.form key="form" onSubmit={send} noValidate className="space-y-4" exit={{
        opacity: 0
      }} data-id="element-1683" data-mp="2l7egmksxvy0x">
            <TextField label="Email" type="email" icon={MailIcon} value={email} onValueChange={setEmail} error={error} autoComplete="email" data-id="element-1684" data-mp="2h3oqhqwm98ds" />
            <BrutalButton type="submit" full size="lg" loading={status === "sending"} data-id="element-1685" data-mp="2c7zq5lcrvngf">
              {status === "sending" ? "Sending…" : "Send reset link"}
            </BrutalButton>
          </motion.form>}
      </AnimatePresence>
    </AuthCard>;
}